import { Link } from "react-router-dom";
import { Github } from "lucide-react";
import { Brand } from "@/components/site/brand";
import { SITE_CONFIG } from "@/config";

const FOOTER_SECTIONS: Array<{
  heading: string;
  links: Array<{ label: string; to: string }>;
}> = [
  {
    heading: "Product",
    links: [
      { label: "Integrations", to: "/integrations" },
      { label: "Pricing", to: "/pricing" },
      { label: "Changelog", to: "/docs/changelog" },
      { label: "Status", to: "https://status.memkernel.com" },
    ],
  },
  {
    heading: "Docs",
    links: [
      { label: "Getting started", to: "/docs/getting-started" },
      { label: "Concepts", to: "/docs/concepts/projects" },
      { label: "REST API", to: "/docs/api/rest" },
      { label: "MCP API", to: "/docs/api/mcp" },
      { label: "TypeScript SDK", to: "/docs/sdks/typescript" },
      { label: "Python SDK", to: "/docs/sdks/python" },
      { label: "Go SDK", to: "/docs/sdks/go" },
    ],
  },
  {
    heading: "Resources",
    links: [
      { label: "Skills", to: "/docs/skills" },
      { label: "Tools", to: "/docs/tools" },
      { label: "Memory", to: "/docs/memory" },
      { label: "Agents", to: "/docs/agents" },
      { label: "Self-hosting", to: "/docs/self-hosting" },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "About", to: "/docs/about" },
      { label: "Privacy", to: "/docs/privacy" },
      { label: "Terms", to: "/docs/terms" },
      { label: "Contact", to: "mailto:hello@memkernel.com" },
    ],
  },
];

export function SiteFooter() {
  return (
    <footer className="mt-24 border-t border-border/40 bg-background">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-5">
          <div className="col-span-2 space-y-4">
            <Brand />
            <p className="max-w-xs text-sm text-muted-foreground">
              {SITE_CONFIG.description}
            </p>
            <div className="flex items-center gap-3">
              <a
                href={SITE_CONFIG.github}
                target="_blank"
                rel="noreferrer"
                aria-label="GitHub"
                className="text-muted-foreground transition-colors hover:text-foreground"
              >
                <Github className="h-5 w-5" />
              </a>
            </div>
          </div>
          {FOOTER_SECTIONS.map((section) => (
            <div key={section.heading} className="space-y-3">
              <h3 className="text-sm font-semibold">{section.heading}</h3>
              <ul className="space-y-2">
                {section.links.map((link) => (
                  <li key={link.to}>
                    {link.to.startsWith("http") || link.to.startsWith("mailto:") ? (
                      <a
                        href={link.to}
                        className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        to={link.to}
                        className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-12 flex flex-col items-start justify-between gap-4 border-t border-border/40 pt-6 text-sm text-muted-foreground md:flex-row md:items-center">
          <p>© {new Date().getFullYear()} MemKernel. All rights reserved.</p>
          <p className="text-xs">
            Personal-account only. No multi-tenant workspaces.
          </p>
        </div>
      </div>
    </footer>
  );
}
